import {formatOrderCar} from '../shared/order-car-display';
import {ChangeDetectionStrategy, Component, OnInit} from '@angular/core';
import {HttpErrorResponse} from '@angular/common/http';
import {NgForm} from '@angular/forms';
import {finalize} from 'rxjs/operators';
import {ApiResponse, MessageCarOption} from '../models/api.models';
import {RestService} from '../services/rest.service';
import {DataService} from '../services/data.service';
import {PermissionService} from '../services/permission.service';
import {AdminPermissions} from '../models/admin-permissions';
import {DialogService} from '../services/dialog.service';

@Component({
    selector: 'app-messages',
    templateUrl: './messages.component.html',
    styleUrls: ['./messages.component.css'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class MessagesComponent implements OnInit {
    title = '';
    message = '';
    isSending = false;
    successMessage = '';
    errorMessage = '';

    carTitle = '';
    carMessage = '';
    isSendingToCar = false;
    carSuccessMessage = '';
    carErrorMessage = '';
    carLoadError = '';
    carOptions: MessageCarOption[] = [];
    filteredCarOptions: MessageCarOption[] = [];
    selectedCarIdentifier = '';
    carSearchText = '';
    isCarDropdownOpen = false;
    isLoadingCars = false;

    constructor(
        private restService: RestService,
        private dataService: DataService,
        private permissionService: PermissionService,
        private dialogService: DialogService
    ) {
    }

    get canBroadcastDriverMessage(): boolean {
        return this.permissionService.has(AdminPermissions.BroadcastDriverMessage);
    }

    get canSendCarMessage(): boolean {
        return this.permissionService.has(AdminPermissions.SendCarMessage);
    }

    ngOnInit(): void {
        if (this.canSendCarMessage) {
            this.loadMessageCars();
        }
    }

    async sendToAllDrivers(form: NgForm): Promise<void> {
        this.successMessage = '';
        this.errorMessage = '';

        if (!this.canBroadcastDriverMessage) {
            this.errorMessage = 'The CAN_BROADCAST_DRIVER_MESSAGE permission is required.';
            return;
        }

        const title = this.title.trim();
        const message = this.message.trim();
        if (form.invalid || title === '' || message === '') {
            this.errorMessage = 'Enter both a title and a message.';
            return;
        }

        const confirmed = await this.dialogService.confirm({
            headerText: 'Message All Drivers',
            bodyText: 'Send this push notification to every driver using the Android app?',
            confirmButtonText: 'Send to all drivers',
            cancelButtonText: 'Cancel',
            danger: false
        });
        if (!confirmed) return;

        this.isSending = true;
        this.restService.messageAllDrivers({Title: title, Message: message}).pipe(
            finalize(() => {
                this.isSending = false;
            })
        ).subscribe({
            next: response => {
                if (!this.dataService.isResponseSuccessful(response)) {
                    this.errorMessage = response.StatusMessage || 'The message could not be sent.';
                    return;
                }

                form.resetForm({title: '', message: ''});
                this.successMessage = response.StatusMessage || 'Message sent to all drivers successfully.';
            },
            error: (error: HttpErrorResponse) => {
                const response = error.error as ApiResponse | undefined;
                this.errorMessage = response && response.StatusMessage
                    ? response.StatusMessage
                    : 'The message could not be sent.';
            }
        });
    }

    loadMessageCars(): void {
        if (this.isLoadingCars) return;

        this.isLoadingCars = true;
        this.carLoadError = '';
        this.restService.getMessageCars().pipe(
            finalize(() => {
                this.isLoadingCars = false;
            })
        ).subscribe({
            next: response => {
                if (!this.dataService.isResponseSuccessful(response)) {
                    this.carLoadError = response.StatusMessage || 'Cars could not be loaded.';
                    return;
                }

                this.carOptions = (response.Cars || []).sort((left, right) =>
                    this.getCarOptionLabel(left).localeCompare(
                        this.getCarOptionLabel(right), undefined, {numeric: true}
                    ));
                this.filterCarOptions(this.carSearchText);
            },
            error: () => {
                this.carLoadError = 'Cars could not be loaded.';
            }
        });
    }

    onCarSearchChanged(value: string): void {
        this.carSearchText = value || '';
        this.selectedCarIdentifier = '';
        this.carSuccessMessage = '';
        this.carErrorMessage = '';
        this.filterCarOptions(this.carSearchText);
        this.isCarDropdownOpen = true;
    }

    toggleCarDropdown(): void {
        if (this.isLoadingCars) return;
        this.filterCarOptions(this.carSearchText);
        this.isCarDropdownOpen = !this.isCarDropdownOpen;
    }

    openCarDropdown(): void {
        if (!this.isLoadingCars) {
            this.filterCarOptions(this.carSearchText);
            this.isCarDropdownOpen = true;
        }
    }

    closeCarDropdown(): void {
        window.setTimeout(() => {
            this.isCarDropdownOpen = false;
        }, 0);
    }

    selectFirstFilteredCar(event: KeyboardEvent): void {
        event.preventDefault();
        if (this.filteredCarOptions.length > 0) {
            this.selectCar(this.filteredCarOptions[0]);
        }
    }

    selectCar(car: MessageCarOption): void {
        this.selectedCarIdentifier = car.CarIdentifier;
        this.carSearchText = this.getCarOptionLabel(car);
        this.isCarDropdownOpen = false;
        this.carSuccessMessage = '';
        this.carErrorMessage = '';
    }

    getCarOptionLabel(car: MessageCarOption): string {
        return formatOrderCar(car);
    }

    async sendToCar(form: NgForm): Promise<void> {
        this.carSuccessMessage = '';
        this.carErrorMessage = '';

        if (!this.canSendCarMessage) {
            this.carErrorMessage = 'The CAN_SEND_CAR_MESSAGE permission is required.';
            return;
        }

        const title = this.carTitle.trim();
        const message = this.carMessage.trim();
        if (this.selectedCarIdentifier === '') {
            this.carErrorMessage = 'Select a car before sending the message.';
            return;
        }
        if (form.invalid || title === '' || message === '') {
            this.carErrorMessage = 'Enter both a title and a message.';
            return;
        }

        const confirmed = await this.dialogService.confirm({
            headerText: 'Message This Car',
            bodyText: 'Send this push notification to this car?',
            confirmButtonText: 'Send to car',
            cancelButtonText: 'Cancel',
            danger: false
        });
        if (!confirmed) return;

        this.isSendingToCar = true;
        this.restService.messageCar({
            CarIdentifier: this.selectedCarIdentifier,
            Title: title,
            Message: message
        }).pipe(
            finalize(() => {
                this.isSendingToCar = false;
            })
        ).subscribe({
            next: response => {
                if (!this.dataService.isResponseSuccessful(response)) {
                    this.carErrorMessage = response.StatusMessage || 'The message could not be sent.';
                    return;
                }

                form.resetForm({carSearch: '', carTitle: '', carMessage: ''});
                this.selectedCarIdentifier = '';
                this.carSearchText = '';
                this.isCarDropdownOpen = false;
                this.filterCarOptions('');
                this.carSuccessMessage = response.StatusMessage || 'Message sent to car successfully.';
            },
            error: (error: HttpErrorResponse) => {
                const response = error.error as ApiResponse | undefined;
                this.carErrorMessage = response && response.StatusMessage
                    ? response.StatusMessage
                    : 'The message could not be sent.';
            }
        });
    }

    private filterCarOptions(value: string): void {
        const search = String(value || '').trim().toLocaleLowerCase();
        this.filteredCarOptions = search === ''
            ? [...this.carOptions]
            : this.carOptions.filter(car => [this.getCarOptionLabel(car), car.CarId, car.CarNumber, car.CompanyIdentifier]
                .some(field => String(field || '').toLocaleLowerCase().includes(search)));
    }
}
